import React from 'react';
import { SummerBanner } from '@/components/SummerBanner';
import { Hero } from '@/components/Hero';
import { PopularCategories } from '@/components/PopularCategories';
import { ExperiencesBanner } from '@/components/ExperiencesBanner';
import { TopRatedBusinesses } from '@/components/TopRatedBusinesses';
import { SummerSeasonOffers } from '@/components/SummerSeasonOffers';
import { TrendingNow, HiddenGems, TonightInCity, TravelStoriesSection, PlanMyTripBanner } from '@/components/DiscoverySections';
import { ExploreDestinations } from '@/components/ExploreDestinations';
import { InvestorStats } from '@/components/InvestorStats';
import { RecommendedForYou } from '@/components/RecommendedForYou';
import { BeachOfWeekStrip } from '@/components/BeachOfWeekStrip';
import { HeadlineCarousel } from '@/components/HeadlineCarousel';
import { SEO } from '@/components/SEO';
import { useCountry } from '@/contexts/CountryContext';
import { BRAND } from '@/lib/brand';
import { useStats } from '@/hooks/useStats';

// Discovery-first homepage: cinematic reference layout + smart sections (Plan My Trip, Trending, Hidden Gems, Stories)
export default function HomePage() {
  const { country } = useCountry();
  const { stats } = useStats(country.code);
  const liveBiz = stats?.listings ?? country.stats.businesses;
  const liveCities = stats?.cities ?? country.stats.cities;
  const liveRating = stats?.avg_rating ?? country.stats.rating;
  const liveReviews = stats?.reviews ?? country.stats.businesses * 8;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "TouristDestination",
      "name": country.name,
      "description": `${country.tagline}. ${BRAND.description}`,
      "image": country.heroImage,
      "url": `https://infopointalbania.al/${country.code}`,
      "touristType": ["Beach", "Cultural", "Adventure", "Culinary", "Nightlife"],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": String(liveRating),
        "reviewCount": String(Math.max(liveReviews, liveBiz)),
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  ];

  return (
    <main className="flex-1 bg-gray-50" data-testid="homepage">
      <SEO
        absolute
        title={`InfoPoint Albania — ${country.tagline}`}
        description={`The premium guide to ${country.name}: ${liveBiz}+ restaurants, hotels, beaches, attractions and hidden gems across ${liveCities} cities. AI itineraries and real reviews.`}
        canonical="/"
        keywords={`InfoPoint Albania, ${country.name} tourism, visit ${country.name}, ${country.name} travel guide, best hotels ${country.name}, ${country.name} restaurants, ${country.name} beaches, Balkans tourism`}
        jsonLd={jsonLd}
      />
      <div className="w-full">
        <SummerBanner />
        <Hero />
        <InvestorStats />
        <BeachOfWeekStrip />
        <HeadlineCarousel />
        <RecommendedForYou />
        <PopularCategories />
        <ExploreDestinations />
        <PlanMyTripBanner />
        <TrendingNow />
        <ExperiencesBanner />
        <HiddenGems />
        <TopRatedBusinesses />
        <TonightInCity />
        <TravelStoriesSection />
        <SummerSeasonOffers />
      </div>
    </main>
  );
}
